import React from "react";
import "./Pricing.css";

export default function Pricing({
  phone = "253.359.7574",
  email = "office@emeraldappraisalservices.com",
  onEstimateClick = () => (window.location.href = "/contact"),
}) {
  const telHref = `tel:+1${phone.replace(/\D/g, "")}`;
  const mailHref = `mailto:${email}`;

  const FEES = [
    { label: "Single Family Residential", amount: 650 },
    { label: "FHA Single Family", amount: 750 },
    { label: "Condo", amount: 700 },
    { label: "FHA Condo", amount: 800 },
    { label: "Exterior Only", amount: 600 },
    { label: "Land Only", amount: 600 },
    { label: "Multi-Family", amount: 900 },
    { label: "Manufactured Home", amount: 700 },
    { label: "Sketches", amount: 150 },
  ];

  const FEATURED = [
    { 
      title: "Single Family", 
      price: 650, 
      desc: "Standard primary residence valuation",
      features: ["USPAP-compliant report", "Market-supported adjustments", "3–5 business days"]
    },
    { 
      title: "Condominium", 
      price: 700, 
      desc: "Includes HOA dues & amenities analysis",
      features: ["HOA document review", "Amenity valuation", "3–5 business days"]
    },
    { 
      title: "2 to 4 Unit", 
      price: 900, 
      desc: "Duplex to fourplex small multi-family",
      features: ["Rental income analysis", "Unit-by-unit valuation", "5–7 business days"]
    },
  ];

  return (
    <section className="Pricing" aria-labelledby="pricing-title">
      <div className="Pricing__bg">
        <div className="Pricing__bg-shape Pricing__bg-shape--1" />
        <div className="Pricing__bg-shape Pricing__bg-shape--2" />
        <div className="Pricing__bg-shape Pricing__bg-shape--3" />
        <div className="Pricing__bg-grid" />
      </div>

      <div className="Pricing__container">
        <header className="Pricing__header">
          <div className="Pricing__eyebrow-wrapper">
            <span className="Pricing__eyebrow">Fee Schedule</span>
          </div>
          <h1 id="pricing-title" className="Pricing__title">
            Transparent, Upfront&nbsp;<span className="Pricing__title-accent">Pricing</span>
          </h1>
          <p className="Pricing__lead">
            Standard fees for common residential appraisal assignments across the Greater Puget Sound.
            Final quotes may vary with property complexity, distance, access, view, waterfront, or
            expedited timelines.
          </p>

          <div className="Pricing__ctaRow">
            <button className="Pricing__cta" onClick={onEstimateClick}>
              <span className="Pricing__cta-text">Request an Appraisal Estimate</span>
              <div className="Pricing__cta-icon">
                <span className="material-icons" aria-hidden="true">arrow_forward</span>
              </div>
            </button>
            <div className="Pricing__contact-links">
              <a className="Pricing__link" href={telHref}>
                <span className="material-icons Pricing__link-icon">phone</span>Call {phone}
              </a>
              <a className="Pricing__link" href={mailHref}>
                <span className="material-icons Pricing__link-icon">email</span>{email}
              </a>
            </div>
          </div>
        </header>

        <div className="Pricing__featured-section">
          <div className="Pricing__featured-header">
            <h2 className="Pricing__featured-title">Most Requested Services</h2>
            <p className="Pricing__featured-subtitle">Quick estimates for common property types</p>
          </div>
          <ul className="Pricing__featured">
            {FEATURED.map((f, i) => (
              <li className="Pricing__card" key={i}>
                <div className="Pricing__card-corner" />
                <div className="Pricing__card-header">
                  <h3 className="Pricing__cardTitle">{f.title}</h3>
                  <div className="Pricing__price">
                    <span className="Pricing__currency">$</span>
                    <span className="Pricing__amount">{f.price}</span>
                  </div>
                </div>
                <p className="Pricing__cardDesc">{f.desc}</p>
                <ul className="Pricing__bullets">
                  {f.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="material-icons Pricing__bulletIcon">check_circle</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className="Pricing__miniBtn" onClick={onEstimateClick}>
                  Get Custom Quote
                  <span className="material-icons" aria-hidden="true">arrow_forward</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* ===== Fee Table ===== */}
        <div className="Pricing__table-section">
          <div className="Pricing__table-header">
            <h2 className="Pricing__table-title">Complete Fee Schedule</h2>
            <p className="Pricing__table-subtitle">Detailed pricing for all appraisal services</p>
          </div>

          <div className="Pricing__tableWrap" role="region" aria-label="Standard fees detail">
            {/* Desktop */}
            <table className="Pricing__table Pricing__table--desktop">
              <colgroup>
                <col style={{ width: "70%" }} />
                <col style={{ width: "30%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="Pricing__colServiceH">Appraisal Service</th>
                  <th scope="col" className="Pricing__colAmtH">Starting From</th>
                </tr>
              </thead>
              <tbody>
                {FEES.map((row, i) => (
                  <tr key={i} className="Pricing__table-row">
                    <th scope="row" className="Pricing__service-name">{row.label}</th>
                    <td className="Pricing__colAmt">
                      <div className="Pricing__price-cell">
                        <span className="Pricing__price-prefix">$</span>
                        {row.amount.toLocaleString()}
                        <span className="Pricing__price-note">USD</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile */}
            <div className="Pricing__table-mobile">
              {FEES.map((row, i) => (
                <div key={i} className="Pricing__mobile-card">
                  <div className="Pricing__mobile-card-header">
                    <h3 className="Pricing__mobile-service">{row.label}</h3>
                  </div>
                  <div className="Pricing__mobile-price">
                    <span className="Pricing__price-prefix">$</span>
                    {row.amount.toLocaleString()}
                    <span className="Pricing__price-note">USD</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="Pricing__note">
              <div className="material-icons Pricing__note-icon">lightbulb</div>
              <div className="Pricing__note-content">
                <strong>For an exact quote including complexities, please contact us for an appraisal fee estimate.</strong>
              </div>
            </div>
          </div>
        </div>

        <section className="Pricing__disclaimers" aria-labelledby="pricing-notes-title">
          <div className="Pricing__disclaimers-header">
            <h2 id="pricing-notes-title" className="Pricing__subTitle">Service Details & Considerations</h2>
            <p className="Pricing__disclaimers-subtitle">Understanding what influences your final appraisal cost</p>
          </div>
          <ul className="Pricing__notesList">
            <li>
              <span className="material-icons Pricing__infoIcon">info</span>
              <div><strong>Complex Properties:</strong> Unique design, acreage, view, or waterfront features may require additional research time and adjusted fees.</div>
            </li>
            <li>
              <span className="material-icons Pricing__infoIcon">info</span>
              <div><strong>Expedited Service:</strong> Rush service available upon request with adjusted timelines and pricing.</div>
            </li>
            <li>
              <span className="material-icons Pricing__infoIcon">info</span>
              <div><strong>Additional Services:</strong> Re-inspection (1004D) and update reports quoted per assignment requirements.</div>
            </li>
            <li>
              <span className="material-icons Pricing__infoIcon">info</span>
              <div><strong>Travel Considerations:</strong> Properties outside our standard service radius may include reasonable mileage surcharges.</div>
            </li>
          </ul>
        </section>
      </div>
    </section>
  );
}
